import * as THREE from 'three';
import { BENCH, SEAT_SPOTS } from '@sea/world';

/**
 * 一条能站两个人的小船。
 *
 * 甲板顶面就是 y = 0，所以玩家脚底/座位高度都以这个平面为基准，
 * 与 @sea/world 的 deck 尺寸一一对应（3.0 × 1.4 m 的圆角矩形）。
 */
export class Boat {
  readonly group = new THREE.Group();
  /** 座位世界锚点（船局部坐标），M1 用来放玩家 */
  readonly seats: THREE.Object3D[] = [];

  constructor() {
    const wood = new THREE.MeshStandardMaterial({ color: 0x6b4f36, roughness: 0.85, metalness: 0.02 });
    const plank = new THREE.MeshStandardMaterial({ color: 0x8a6a48, roughness: 0.9 });
    const dark = new THREE.MeshStandardMaterial({ color: 0x3b2c20, roughness: 0.95 });

    this.group.add(this.buildHull(wood));
    this.group.add(this.buildDeck(plank));
    this.group.add(this.buildBenches(dark));
    this.group.add(this.buildMast(dark, plank));
    this.group.add(this.buildLantern());
    this.group.add(this.buildRailings(dark));
  }

  /** 船体半宽相对旧版（0.7）的放大系数：整体加宽约 0.5 m */
  private static readonly BEAM = 1.9 / 1.4;

  private buildHull(material: THREE.Material): THREE.Mesh {
    const shape = new THREE.Shape();
    // 形状平面里 +Y 是船头；旋转后 +Y → -Z，正好对上「船头朝 -Z」的约定
    shape.moveTo(-0.07, 2.25);
    shape.bezierCurveTo(0.46, 1.55, 0.72, 0.45, 0.7, -1.05);
    shape.lineTo(0.6, -2.0);
    shape.quadraticCurveTo(0, -2.32, -0.6, -2.0);
    shape.lineTo(-0.7, -1.05);
    shape.bezierCurveTo(-0.72, 0.45, -0.46, 1.55, -0.07, 2.25);

    const geo = new THREE.ExtrudeGeometry(shape, {
      depth: 0.62,
      bevelEnabled: true,
      bevelSize: 0.035,
      bevelThickness: 0.035,
      bevelSegments: 1,
      curveSegments: 14,
    });
    geo.rotateX(-Math.PI / 2); // 立起来：长度沿 Z，吃水沿 Y
    geo.scale(Boat.BEAM, 1, 1); // 加宽船体
    geo.translate(0, -0.62, 0); // 甲板顶面对齐 y = 0

    const mesh = new THREE.Mesh(geo, material);
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    return mesh;
  }

  private buildDeck(material: THREE.Material): THREE.Mesh {
    // 比船舷内侧略小一圈的甲板板
    const w = 0.9;
    const l = 1.98;
    const geo = new THREE.BoxGeometry(w * 2, 0.04, l * 2);
    const mesh = new THREE.Mesh(geo, material);
    mesh.position.set(0, 0.001, 0.02);
    return mesh;
  }

  private buildBenches(material: THREE.Material): THREE.Group {
    const group = new THREE.Group();

    // 一条横贯船尾的长凳，两个座位并排坐上去；船头一侧整片留空供站立走动
    const bench = new THREE.Mesh(new THREE.BoxGeometry(BENCH.halfWidth * 2, 0.08, BENCH.halfDepth * 2), material);
    bench.position.set(0, 0.42, BENCH.z);
    group.add(bench);

    for (const dx of [-0.5, 0.5]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.42, BENCH.halfDepth * 1.5), material);
      leg.position.set(dx, 0.21, BENCH.z);
      group.add(leg);
    }

    for (const spot of SEAT_SPOTS) {
      const anchor = new THREE.Object3D();
      anchor.name = 'seat';
      anchor.position.set(spot.x, 0.46, spot.z);
      group.add(anchor);
      this.seats.push(anchor);
    }

    return group;
  }

  /**
   * 船舷围栏：两侧各一排立柱 + 两道横杆。
   * 立柱沿船体轮廓（与 buildHull 同一条 bezier）取样，取样点属于「建一次就不动」的几何体，
   * 不违反"运行时不新建几何体"的约定。
   */
  private buildRailings(material: THREE.Material): THREE.Group {
    const group = new THREE.Group();

    const shape = new THREE.Shape();
    shape.moveTo(-0.07, 2.25);
    shape.bezierCurveTo(0.46, 1.55, 0.72, 0.45, 0.7, -1.05);
    shape.lineTo(0.6, -2.0);
    shape.quadraticCurveTo(0, -2.32, -0.6, -2.0);
    shape.lineTo(-0.7, -1.05);
    shape.bezierCurveTo(-0.72, 0.45, -0.46, 1.55, -0.07, 2.25);

    // 轮廓点（形状平面里 +Y 是船头，转成船的 (x, z) 后 z = -y）
    const outline = shape.getPoints(40).map((pt) => ({ x: pt.x * Boat.BEAM, z: -pt.y }));

    // 只要船体两侧（|x| 大于 0.45），按 z 排序后均匀取点
    const posts: { x: number; z: number }[] = [];
    const side = outline.filter((pt) => pt.x > 0.5).sort((a, b) => b.z - a.z);
    const step = Math.max(1, Math.floor(side.length / 7));
    for (let i = 0; i < side.length; i += step) {
      const pt = side[i];
      if (pt) posts.push(pt);
    }

    const railHeight = 0.72;
    const railGeo = new THREE.CylinderGeometry(0.018, 0.018, 1, 6);
    const postGeo = new THREE.CylinderGeometry(0.022, 0.026, railHeight, 6);

    const addSide = (mirror: number): void => {
      const points = posts.map((pt) => new THREE.Vector3(pt.x * mirror, 0, pt.z));
      for (const point of points) {
        const post = new THREE.Mesh(postGeo, material);
        post.position.set(point.x, railHeight / 2, point.z);
        group.add(post);
      }
      // 两道横杆
      for (const h of [railHeight * 0.62, railHeight]) {
        for (let i = 0; i < points.length - 1; i++) {
          const a = points[i]!;
          const b = points[i + 1]!;
          const dir = b.clone().sub(a);
          const len = dir.length();
          if (len < 0.05) continue;
          const bar = new THREE.Mesh(railGeo, material);
          bar.scale.y = len;
          bar.position.set((a.x + b.x) / 2, h, (a.z + b.z) / 2);
          bar.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
          group.add(bar);
        }
      }
    };

    addSide(1);
    addSide(-1);

    return group;
  }

  private buildMast(material: THREE.Material, flagMaterial: THREE.Material): THREE.Group {
    const group = new THREE.Group();

    // 桅杆放在船头附近：从坐姿视线看过去它在画面边缘，不会挡住海与岛
    const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.06, 3.0, 8), material);
    mast.position.set(0, 1.5, -1.86);
    group.add(mast);

    // 一面小旗：给「船在走」一个可读的视觉线索（比帆省面数、也不挡视线）
    const flagShape = new THREE.Shape();
    flagShape.moveTo(0, 0);
    flagShape.lineTo(0.62, -0.16);
    flagShape.lineTo(0, -0.34);
    flagShape.lineTo(0, 0);
    const flag = new THREE.Mesh(
      new THREE.ShapeGeometry(flagShape),
      new THREE.MeshStandardMaterial({ color: 0xb8503c, roughness: 0.85, side: THREE.DoubleSide }),
    );
    flag.position.set(0.04, 2.92, -1.86);
    group.add(flag);

    return group;
  }

  private buildLantern(): THREE.Group {
    const group = new THREE.Group();

    const glass = new THREE.MeshStandardMaterial({
      color: 0xffc98a,
      emissive: new THREE.Color(0xffb066),
      emissiveIntensity: 2.2,
      roughness: 0.4,
    });
    const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.075, 12, 10), glass);
    bulb.position.set(0, 0.72, -1.92);
    group.add(bulb);

    const cage = new THREE.Mesh(
      new THREE.CylinderGeometry(0.1, 0.1, 0.2, 8, 1, true),
      new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.8, side: THREE.DoubleSide }),
    );
    cage.position.copy(bulb.position);
    group.add(cage);

    const light = new THREE.PointLight(0xffb066, 3.2, 7, 2);
    light.position.copy(bulb.position);
    group.add(light);

    return group;
  }
}
